<script setup lang="ts">
import { computed, watch } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import { defaultExpenses, defaultPeriodicValue } from '@/helpers/builder';
import { forceScrollToTop } from '@/helpers/forceScrollToTop';

import type { ExpenseTypeDto } from '@/api/backend/public/expense-types/expense-type.dto';
import type { ApplicantExpense } from '@/types';

interface SimpleExpenseFormValues {
  groceries: {
    amount: number;
    frequency: 'month';
    expenseTypeId?: string;
  };
  numberOfDependants: string;
}

const props = defineProps<{
  applicantId: string;
  modelValue: ApplicantExpense | null;
  expenseTypes: ExpenseTypeDto[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: ApplicantExpense): void;
}>();

const dependantOptions = {
  '0': '0',
  '1': '1',
  '2': '2',
  '3': '3+',
};

const groceriesTypeId = computed(() => props.expenseTypes.find((e) => e.type === 'groceries')?.id);

const simpleExpensesSchema = yup.object({
  groceries: yup
    .object({
      amount: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .required('Monthly repayments amount is required')
        .min(0, 'Amount cannot be negative'),
      frequency: yup.string().required(),
    })
    .required(),
  numberOfDependants: yup
    .number()
    .required('Number of dependants is required')
    .min(0, 'Number of dependants is required'),
});

const { meta, submitCount, handleSubmit, resetForm } = useForm<SimpleExpenseFormValues>({
  validationSchema: simpleExpensesSchema,
  validateOnMount: false,
});

watch(
  () => props.modelValue,
  (newVal) => {
    const formValues: SimpleExpenseFormValues = {
      groceries: {
        amount: defaultPeriodicValue().amount,
        frequency: 'month',
        expenseTypeId: groceriesTypeId.value,
      },
      numberOfDependants: '0',
    };

    if (newVal) {
      const id = groceriesTypeId.value;
      let monthlyExpensesAmount: number | undefined;

      if (newVal.groceries && typeof newVal.groceries === 'object' && 'amount' in newVal.groceries) {
        monthlyExpensesAmount = (newVal.groceries as { amount: number }).amount;
        formValues.groceries.expenseTypeId = (newVal.groceries as { expenseTypeId?: string }).expenseTypeId;
      } else if (id && newVal.fundsExpenses) {
        const foundExpense = newVal.fundsExpenses.find((expense) => expense.expenseTypeId === id);
        monthlyExpensesAmount = foundExpense?.expenseAmountApplicationValue;
      }

      formValues.groceries.amount = monthlyExpensesAmount ?? defaultPeriodicValue().amount;
      formValues.numberOfDependants = newVal.numberOfDependants.toString() ?? '0';
    }

    resetForm({ values: formValues });
  },
  { immediate: true, deep: true },
);

const update = handleSubmit(
  (formValues) => {
    const updatedExpenses: ApplicantExpense = cloneDeep(props.modelValue ?? defaultExpenses());
    const id = groceriesTypeId.value;

    if (!id) return false;

    updatedExpenses.groceries = {
      expenseTypeId: id,
      amount: formValues.groceries.amount,
      frequency: 'month',
    };

    updatedExpenses.numberOfDependants = parseInt(formValues.numberOfDependants);

    emit('update:modelValue', updatedExpenses);
    forceScrollToTop(0);
    return true;
  },
  () => {
    forceScrollToTop(0);
    return false;
  },
);

defineExpose({ update });
</script>

<template>
  <div class="flex w-full gap-4">
    <BxField v-slot="{ field }" name="groceries.amount">
      <BxInput
        v-bind="field"
        :id="`${applicantId}-monthly-expenses`"
        class="flex-1 pt-1"
        type="number"
        label="Monthly repayments amount"
        label-type="floating"
        leading-symbol="$"
      />
    </BxField>
    <BxField v-slot="{ field }" name="numberOfDependants">
      <BxSelect
        v-bind="field"
        :id="`${applicantId}-dependants`"
        :list-items="dependantOptions"
        class="flex-1"
        label="Dependants"
        label-type="floating"
      />
    </BxField>
  </div>
  <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
    Please review the error messages above.
  </BxErrorPanel>
</template>
